/** Editor ⁄ Change-history tab pair for form dialogs that edit one tracked
 * record — the repeatable half of the LaunchPolicyFormDialog pattern.
 *
 * Usage in a form dialog:
 *   const history = useEditorHistoryTabs({
 *     enabled: isEdit && hasPerm('console_audit_view'),
 *     contentType: 'demo.notification_theme',
 *     objectPk: theme?.id ?? null,
 *     onRestored: () => void qc.invalidateQueries({ queryKey: ['themes'] }),
 *   });
 *   // in the open-reset effect:      history.reset();
 *   // in the dialog body:            {history.header}
 *   //                                {history.historyPane ?? <ExistingForm/>}
 *   // in the dialog actions:         history.showingHistory ? <Close> : <Save/Cancel>
 *
 * Pages/dialogs that already have their own tab bar (TourDetailPage,
 * AccessGroupEditDialog) mount <HistoryPanel> directly instead.
 */
import { Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { HistoryPanel } from './HistoryPanel';

export function useEditorHistoryTabs({
  enabled,
  contentType,
  objectPk,
  aggregate,
  onRestored,
}: {
  /** Editing an existing record AND the viewer holds console_audit_view. */
  enabled: boolean;
  /** Backend `app_label.model` label, e.g. "demo.notification_theme". */
  contentType: string;
  objectPk: string | number | null | undefined;
  /** Include child-model rows grouped under this object (HistoryPanel default: true). */
  aggregate?: boolean;
  onRestored?: () => void;
}): {
  header: ReactNode;
  historyPane: ReactNode;
  showingHistory: boolean;
  reset: () => void;
} {
  const [tab, setTab] = useState(0);
  const show = enabled && objectPk !== null && objectPk !== undefined;
  const showingHistory = show && tab === 1;
  return {
    reset: () => setTab(0),
    showingHistory,
    header: show ? (
      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="Editor" />
        <Tab label="Change history" />
      </Tabs>
    ) : null,
    historyPane: showingHistory ? (
      <HistoryPanel
        contentType={contentType}
        objectPk={String(objectPk)}
        {...(aggregate !== undefined ? { aggregate } : {})}
        {...(onRestored !== undefined ? { onRestored } : {})}
      />
    ) : null,
  };
}
